import type { HubId, Navigation, NavigationPageItem } from 'types/navigation'
import { HUB_ORDER, HUBS } from 'constants/hubs'

export interface HubPage {
  title: string
  segment: string
  path: string
  icon?: NavigationPageItem['icon']
  href?: string
  hub: HubId
  slashes?: string[]
  keywords?: string[]
  description?: string
}

const isPage = (item: Navigation[number]): item is NavigationPageItem =>
  item.kind !== 'header' && item.kind !== 'divider'

/** Walk the nav tree and collect pages that declare a hub (including Tools children). */
export function collectHubPages(items: Navigation, parentSegment?: string): HubPage[] {
  const pages: HubPage[] = []

  for (const item of items) {
    if (!isPage(item)) continue

    if (item.children && item.children.length > 0) {
      pages.push(...collectHubPages(item.children, item.segment))
      continue
    }

    if (!item.hub || !item.segment) continue

    const segment =
      parentSegment && item.segment ? `${parentSegment}/${item.segment}` : item.segment
    pages.push({
      title: item.title,
      segment,
      path: `/${segment}`,
      icon: item.icon,
      href: item.href,
      hub: item.hub,
      slashes: item.slashes,
      keywords: item.keywords,
      description: item.description,
    })
  }

  return pages
}

export function pagesForHub(items: Navigation, hubId: HubId): HubPage[] {
  return collectHubPages(items).filter(page => page.hub === hubId)
}

export function hubForPath(items: Navigation, pathname: string): HubId | null {
  const normalized = pathname.replace(/\/+$/, '') || '/'
  for (const page of collectHubPages(items)) {
    if (normalized === page.path || normalized.startsWith(`${page.path}/`)) {
      return page.hub
    }
  }
  return null
}

export function firstPageInHub(items: Navigation, hubId: HubId): HubPage | null {
  return pagesForHub(items, hubId)[0] ?? null
}

export function visibleHubs(items: Navigation): HubId[] {
  const present = new Set(collectHubPages(items).map(page => page.hub))
  return HUB_ORDER.filter(id => present.has(id))
}

export function hubTitle(hubId: HubId): string {
  return HUBS[hubId].title
}
